import React from 'react';

import { colorSystem } from '../../theme';
import { Text } from '../Text/Text';

export interface TagProps {
    color?: keyof typeof colorSystem;
    label: string | JSX.Element;
    attention?: 'high' | 'low' | 'formula';
    style?: React.CSSProperties;
    /** Truncates the label, with the full text on hover; fits its parent's width, so give the parent one. */
    showEllipsis?: boolean;
}

const defaultStyle: React.CSSProperties = {
    width: 'fit-content',
    height: 'fit-content',
    cursor: 'inherit',
    whiteSpace: 'nowrap',
    borderRadius: '4px',
};

/** `text-overflow: ellipsis` needs a block box constrained to its container. */
const ellipsisStyle: React.CSSProperties = {
    display: 'block',
    maxWidth: '100%',
};

export const Tag = React.forwardRef<HTMLSpanElement, TagProps>(
    (
        { label, color = 'neutral', attention = 'high', style, showEllipsis = false, ...props }: TagProps,
        ref,
    ): JSX.Element => {
        const ellipsisContainerStyle = showEllipsis ? ellipsisStyle : undefined;
        switch (attention) {
            case 'high':
                return (
                    <Text
                        ref={ref}
                        variant="h5"
                        color={colorSystem[color][8]}
                        style={{
                            ...defaultStyle,
                            padding: '2px 4px',
                            backgroundColor: colorSystem[color][2],
                            ...ellipsisContainerStyle,
                            ...style,
                        }}
                        showEllipsis={showEllipsis}
                        {...props}
                    >
                        {label}
                    </Text>
                );
            case 'low':
                return (
                    <Text
                        ref={ref}
                        variant="body-small"
                        color={colorSystem[color][8]}
                        style={{
                            ...defaultStyle,
                            padding: '1px 3px',
                            backgroundColor: colorSystem[color][1],
                            border: `1px solid ${colorSystem[color][2]}`,
                            ...ellipsisContainerStyle,
                            ...style,
                        }}
                        showEllipsis={showEllipsis}
                        {...props}
                    >
                        {label}
                    </Text>
                );
            case 'formula':
                return (
                    <Text
                        ref={ref}
                        variant="body-small"
                        color={colorSystem[color][color === 'neutral' || color === 'yellow' ? 8 : 7]}
                        style={{
                            ...defaultStyle,
                            padding: '1px 3px',
                            backgroundColor: colorSystem.neutral[1],
                            border: `1px solid ${colorSystem.neutral[2]}`,
                            ...ellipsisContainerStyle,
                            ...style,
                        }}
                        showEllipsis={showEllipsis}
                        {...props}
                    >
                        {label}
                    </Text>
                );
        }
    },
);
